"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
const SubscriptionHandler_1 = require("../../../Framework/SubscriptionHandler");
const TypeGuard_1 = require("../../../Types/TypeGuard");
const Bind_1 = require("../../../Utility/Decorator/Bind");
const Loader_1 = require("../../../Components/Loader/Loader");
const DialogUI_1 = require("../../../Components/Window/Dialog/DialogUI");
const DialogUtil_1 = require("../../../Components/Window/Dialog/DialogUtil");
const DefaultButtonHandler_1 = require("../../../Components/Window/Dialog/Event/DefaultButtonHandler");
const OverlayClickHandler_1 = require("../../../Components/Window/Dialog/Event/OverlayClickHandler");
const WindowManager_1 = require("../../../Components/Window/WindowManager");
class Dialog {
    constructor(options, ui) {
        this.subscriptionHandler = new SubscriptionHandler_1.default();
        /** state */
        this.showing = false;
        this.destroyed = false;
        this.options = options;
        this.ui = ui !== null && ui !== void 0 ? ui : new DialogUI_1.default(options);
        this.ui.closeButton.addEventListener('click', this.close);
        this.loader = new Loader_1.default(this.ui.content);
        this.onKeyEscape = (evt) => {
            if (evt.key === 'Escape' && WindowManager_1.default.default.hasFocus(this)) {
                evt.stopImmediatePropagation();
                evt.preventDefault();
                this.close();
            }
        };
        this.onKeyEnter = (evt) => {
            if (evt.key !== 'Enter' || this.defaultButtonHandler === undefined || WindowManager_1.default.default.hasFocus(this) === false) {
                return;
            }
            if (evt.target.tagName === 'TEXTAREA' && evt.ctrlKey === false) {
                return;
            }
            this.defaultButtonHandler.handleEvent(evt);
        };
        // when viewport changes, update frame window
        this.onViewportResize = () => {
            if (this.options.fitViewport !== false) {
                DialogUtil_1.default.fitViewport(this.ui, this.options);
            }
            DialogUtil_1.default.center(this.ui.frame, this.ui.overlay);
        };
        if (this.options.content !== undefined) {
            this.setContent(this.options.content);
        }
        if (this.options.footer !== undefined) {
            this.setFooter(this.options.footer);
        }
        if (this.options.closeOnClickOverlay !== false && this.options.closable !== false) {
            new OverlayClickHandler_1.default(this.ui).onClick(() => this.close());
        }
        if (this.options.autoCloseAfter !== undefined && this.options.autoCloseAfter > 0) {
            this.subscriptionHandler.start(this.close, this.options.autoCloseAfter);
        }
    }
    /**
     * Change the title of the dialog. For setting the initial title use DialogOptions in the constructor
     */
    setTitle(title) {
        this.options.title = title;
        this.ui.setTitle(title);
        return this;
    }
    /**
     * Set the contents of the dialog with the given contents
     */
    setContent(content) {
        if (typeof content === 'string') {
            this.ui.content.innerHTML = content;
        }
        else {
            this.ui.content.innerHTML = '';
            this.ui.content.append(content);
        }
        return this;
    }
    /**
     * Set the contents of the dialog with the given contents
     */
    setFooter(content) {
        if (typeof content === 'string') {
            this.ui.footer.innerHTML = content;
        }
        else {
            this.ui.footer.innerHTML = '';
            this.ui.footer.append(content);
        }
        return this;
    }
    /**
     * Position the dialog in the center viewport
     */
    centerInViewport() {
        DialogUtil_1.default.center(this.ui.frame, this.ui.overlay);
        return this;
    }
    /**
     * Load given view into the dialog and show the dialog.
     */
    showView(view) {
        const async = TypeGuard_1.default.isLoadable(view);
        let promise = Promise.resolve();
        if (async) {
            promise = view.load().then(() => {
                this.loader.stop();
                if (this.options.fitViewport !== false) {
                    DialogUtil_1.default.fitViewport(this.ui, this.options);
                }
                // after load, position dialog correctly
                DialogUtil_1.default.center(this.ui.frame, this.ui.overlay);
            });
        }
        if (TypeGuard_1.default.isDOMInsertable(view)) {
            view.insertInto(this.ui.content);
        }
        this.show();
        if (async) {
            this.loader.start();
        }
        return promise;
    }
    show() {
        if (this.beforeShowListener !== undefined && this.beforeShowListener() === false) {
            return this;
        }
        this.ui.setupUI(this.options).setupIcon(this.options);
        // if there's an active element, make sure focus moves to dialog
        if (TypeGuard_1.default.isBlurrable(document.activeElement)) {
            this.focusElement = document.activeElement;
            this.focusElement.blur();
        }
        DialogUtil_1.default.setupDimensions(this.ui.content, this.options);
        // show dialog. This needs to be done _before_ doing calculations on the element for positioning
        this.ui.show();
        this.showing = true;
        // register dialog
        WindowManager_1.default.default.register(this);
        // adjust size if necessary
        if (this.options.fitViewport !== false) {
            DialogUtil_1.default.fitViewport(this.ui, this.options);
        }
        // set position
        DialogUtil_1.default.center(this.ui.frame, this.ui.overlay);
        // add escape listener
        if (this.options.closeOnEscape !== false && this.options.closable !== false) {
            document.addEventListener('keyup', this.onKeyEscape);
        }
        // register default button
        // request focus to default button
        if (this.defaultButtonHandler) {
            document.addEventListener('keyup', this.onKeyEnter);
            this.defaultButtonHandler.getButton().focus();
        }
        // listen to window change
        window.addEventListener('resize', this.onViewportResize);
        if (this.showListener !== undefined) {
            this.showListener();
        }
        return this;
    }
    hide() {
        if (this.beforeHideListener !== undefined && this.beforeHideListener() === false) {
            return this;
        }
        this.ui.hide();
        this.showing = false;
        document.removeEventListener('keyup', this.onKeyEscape);
        document.removeEventListener('keyup', this.onKeyEnter);
        document.removeEventListener('resize', this.onViewportResize);
        if (this.options.destroyOnClose !== false) {
            this.destroy();
        }
        WindowManager_1.default.default.unregister(this);
        // restore focus to previous active element
        if (this.focusElement) {
            this.focusElement.focus();
            this.focusElement = undefined;
        }
        if (this.hideListener !== undefined) {
            this.hideListener();
        }
        return this;
    }
    toggle() {
        if (this.destroyed) {
            return this;
        }
        if (this.showing) {
            this.hide();
        }
        else {
            this.show();
        }
        return this;
    }
    destroy() {
        this.ui.destroy();
        this.destroyed = true;
        return this;
    }
    /**
     * When `enter` key is pressed, the action of this button will be triggered
     */
    setDefaultButton(button, align = 'R') {
        this.defaultButtonHandler = new DefaultButtonHandler_1.default(button);
        this.addButton(button, align);
        return this;
    }
    /**
     * Add a close button. Will hide the dialog when pressed.
     */
    addCloseButton(button, align = 'R') {
        button.onClick(this.close);
        this.addButton(button, align);
        return this;
    }
    /**
     * Add a button to the bottom bar of the dialog
     */
    addButton(button, align = 'R') {
        if (align === 'L') {
            button.style.cssFloat = 'left';
        }
        this.ui.footer.append(button);
        return this;
    }
    /**
     * Called _just_ before the dialog will be shown. If false is returned the showing of the dialog will be prevented
     */
    onBeforeShow(handler) {
        this.beforeShowListener = handler;
        return this;
    }
    /**
     * Called after the dialog was shown
     */
    onShow(handler) {
        this.showListener = handler;
        return this;
    }
    /**
     * Called _just_ before the dialog will be hidden. If false is returned the hiding of the dialog will be prevented
     */
    onBeforeHide(handler) {
        this.beforeHideListener = handler;
        return this;
    }
    /**
     * Called after the dialog was hidden
     */
    onHide(handler) {
        this.hideListener = handler;
        return this;
    }
    /**
     * Called _just_ before the close action will be triggered. This event will only be fired when a close action is
     * trigger:
     * - the window cross button
     * - the dialog close button
     * - clicked on the overlay
     * - pressed escape
     */
    onBeforeClose(handler) {
        this.beforeCloseListener = handler;
        return this;
    }
    /**
     * Called after the close action was executed. See `beforeClose` for more details
     * trigger:
     * - the window cross button
     * - the dialog close button
     * - clicked on the overlay
     * - pressed escape
     */
    onClose(handler) {
        this.closeListener = handler;
        return this;
    }
    /**
     * Called only by close actions (closeButton, escape, clickOverlay, closeWindow)
     */
    close() {
        this.subscriptionHandler.stop();
        if (this.beforeCloseListener !== undefined && this.beforeCloseListener() === false) {
            return this;
        }
        this.hide();
        if (this.closeListener !== undefined) {
            this.closeListener();
        }
        return this;
    }
}
exports.default = Dialog;
__decorate([
    Bind_1.default
], Dialog.prototype, "show", null);
__decorate([
    Bind_1.default
], Dialog.prototype, "hide", null);
__decorate([
    Bind_1.default
], Dialog.prototype, "close", null);
//# sourceMappingURL=Dialog.js.map